import React, { useEffect, useMemo, useState } from 'react';

import {
  ArrowRight,
  Check,
  CircleCheck,
  Flame,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';

import {
  useMyCoursesPlayed,
  usePlaceMyCoursePlayed,
} from '../../hooks/useMemberCoursesPlayedApi';
import { MemberCoursesPlayedSummaryDTO } from '../../services/apiClient';
import { showToast } from '../../utils/showToast';
import CourseLogo from '../common/CourseLogo';
import ScoreBadge from '../member/ScoreBadge';

// The ranking game. One unranked course at a time, in three steps:
//
//   1. "How did you like the course?" Meh, Good or Great. That picks the course's group: Great
//      courses always sit above Good ones, Good above Meh, and each group has its own score band.
//   2. "Which do you like more?" The course against one already in its group, again and again,
//      each answer halving the places it could still go (a binary search), so a course finds its
//      place among 30 in about five taps. Pass means "about the same" and settles it there.
//   3. The result: its rank, its neighbours, its score, and the way on to the next course.
//
// The comparisons happen here, in the browser, and only the outcome is saved
// (PlaceMyCoursePlayed), so a member who closes the drawer halfway has changed nothing. The backend
// clamps the place to the sentiment's group regardless, so a stale list cannot misplace a course.
//
// Skip This Course only hides a course for the rest of this visit: the parent holds the skipped
// ids, and the course is back in the strip next time.

// CourseSentiment on the backend. A higher number is a better feeling.
const MEH = 1;
const GOOD = 2;
const GREAT = 3;

const SENTIMENTS = [
  { value: MEH, label: 'Meh...', Icon: ThumbsDown },
  { value: GOOD, label: 'Good', Icon: ThumbsUp },
  { value: GREAT, label: 'Great!', Icon: Flame },
];

// A ranked course with no sentiment was ranked before sentiments existed. The backend treats it as
// Good, so this does too, or the two would disagree about where the groups start.
const sentimentOf = (row: MemberCoursesPlayedSummaryDTO): number =>
  row.sentiment ?? GOOD;

const keyOf = (row: MemberCoursesPlayedSummaryDTO): string =>
  row.courseId?.toLowerCase() ?? '';

const whereOf = (row: MemberCoursesPlayedSummaryDTO): string | undefined =>
  [row.stateName, row.countryName]
    .filter((part) => part != null && part.trim().length > 0)
    .join(', ') ||
  row.courseAddress ||
  undefined;

type Step = 'feel' | 'compare' | 'result';

// The search range over the ranked list: the course goes somewhere from index `low` to `high`
// (both inclusive of the "after the last one" slot), in the list without the course itself.
interface Range {
  low: number;
  high: number;
}

interface RankCourseDrawerProps {
  isOpen: boolean;
  // The course to start with: one tapped in the unranked strip. Null starts with the first
  // unranked course that has not been skipped.
  startCourseId: string | null;
  // Courses skipped on this visit, lower-cased. Owned by the parent so they stay skipped while
  // the drawer is closed and opened again.
  skippedIds: Set<string>;
  onSkip: (courseId: string) => void;
  onClose: () => void;
}

const RankCourseDrawer: React.FC<RankCourseDrawerProps> = ({
  isOpen,
  startCourseId,
  skippedIds,
  onSkip,
  onClose,
}) => {
  const { data: coursesPlayed } = useMyCoursesPlayed();
  const placeCourse = usePlaceMyCoursePlayed();

  const [courseKey, setCourseKey] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('feel');
  const [sentiment, setSentiment] = useState<number | null>(null);
  const [range, setRange] = useState<Range>({ low: 0, high: 0 });
  const [chosen, setChosen] = useState<'course' | 'other' | null>(null);

  const all = useMemo(() => coursesPlayed ?? [], [coursesPlayed]);

  const unranked = useMemo(
    () => all.filter((row) => row.rankOrder == null),
    [all],
  );

  const course = useMemo(
    () => all.find((row) => keyOf(row) === courseKey) ?? null,
    [all, courseKey],
  );

  // The ranked courses, favourite first, without the one being ranked.
  const ranked = useMemo(
    () =>
      all
        .filter((row) => row.rankOrder != null && keyOf(row) !== courseKey)
        .sort((a, b) => (a.rankOrder ?? 0) - (b.rankOrder ?? 0)),
    [all, courseKey],
  );

  const nextUnranked = (exclude: string | null): string | null =>
    unranked
      .map(keyOf)
      .find((key) => key !== exclude && !skippedIds.has(key)) ?? null;

  const startCourse = (key: string | null): void => {
    setCourseKey(key);
    setStep('feel');
    setSentiment(null);
    setChosen(null);
  };

  // A fresh start every time the drawer opens. Not on every re-read of the list: placing a course
  // re-reads it, and the result screen must stay on the course that was just ranked.
  useEffect(() => {
    if (isOpen) {
      startCourse(startCourseId?.toLowerCase() ?? nextUnranked(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on open only, see above
  }, [isOpen, startCourseId]);

  const save = (pickedSentiment: number, position: number): void => {
    if (course?.courseId == null) {
      return;
    }

    placeCourse.mutate(
      { courseId: course.courseId, sentiment: pickedSentiment, position },
      {
        onSuccess: () => setStep('result'),
        onError: (error: unknown) => {
          showToast('Could not save your ranking.', 'error');
          console.error('Could not rank that course:', error);
        },
      },
    );
  };

  // A settled window is a place. Otherwise there is another course to compare against.
  const settleOrCompare = (pickedSentiment: number, next: Range): void => {
    if (next.low >= next.high) {
      save(pickedSentiment, next.low + 1);
      return;
    }
    setRange(next);
    setChosen(null);
    setStep('compare');
  };

  const handleFeel = (value: number): void => {
    setSentiment(value);
    // The group: after every better course, up to the last one as good or better. An empty group
    // settles at once: the first Great course goes straight to the top of the Goods, say.
    const low = ranked.filter((row) => sentimentOf(row) > value).length;
    const high = ranked.filter((row) => sentimentOf(row) >= value).length;
    settleOrCompare(value, { low, high });
  };

  const middle = Math.floor((range.low + range.high) / 2);
  const opponent = step === 'compare' ? ranked[middle] : undefined;

  const handlePick = (prefersCourse: boolean): void => {
    if (sentiment == null) {
      return;
    }
    setChosen(prefersCourse ? 'course' : 'other');
    // A short beat with the tick showing, so the choice reads as made before the next card.
    const next = prefersCourse
      ? { low: range.low, high: middle }
      : { low: middle + 1, high: range.high };
    setTimeout(() => settleOrCompare(sentiment, next), 250);
  };

  // About the same: straight after the course it was compared with.
  const handlePass = (): void => {
    if (sentiment == null) {
      return;
    }
    save(sentiment, middle + 2);
  };

  const handleSkip = (): void => {
    if (courseKey == null) {
      return;
    }
    onSkip(courseKey);
    const next = nextUnranked(courseKey);
    if (next == null) {
      onClose();
    } else {
      startCourse(next);
    }
  };

  const handleNext = (): void => {
    const next = nextUnranked(courseKey);
    if (next == null) {
      onClose();
    } else {
      startCourse(next);
    }
  };

  const remaining = unranked.filter(
    (row) => keyOf(row) !== courseKey && !skippedIds.has(keyOf(row)),
  ).length;

  const header = (
    <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-white/10">
      <p className="text-sm font-bold uppercase tracking-wider text-ink dark:text-white">
        Course ranking
      </p>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="rounded-lg p-1 text-ink-muted transition-colors hover:bg-gray-100 hover:text-ink dark:text-white/50 dark:hover:bg-white/10 dark:hover:text-white"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );

  const courseCard = course != null && (
    <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3 dark:bg-white/5">
      <CourseLogo
        logoFileId={course.courseLogoFileId}
        name={course.courseName ?? ''}
        closed={course.courseClosed}
        className="h-14 w-14 shrink-0 rounded-lg"
      />
      <div className="min-w-0">
        <p className="truncate text-sm font-bold uppercase tracking-wide text-ink dark:text-white">
          {course.courseName}
        </p>
        <p className="truncate text-xs text-ink-muted dark:text-white/50">
          {whereOf(course)}
        </p>
      </div>
    </div>
  );

  const choiceCard = (
    row: MemberCoursesPlayedSummaryDTO,
    side: 'course' | 'other',
  ): React.ReactElement => (
    <button
      type="button"
      onClick={() => handlePick(side === 'course')}
      disabled={chosen != null || placeCourse.isPending}
      className={`relative flex min-w-0 flex-1 flex-col items-start gap-3 rounded-xl border-2 p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        chosen === side
          ? 'border-primary bg-primary/10'
          : side === 'course'
            ? 'border-dashed border-gray-300 hover:border-primary dark:border-white/20'
            : 'border-gray-200 hover:border-primary dark:border-white/10'
      }`}
    >
      {chosen === side && (
        <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-secondary">
          <Check className="h-4 w-4" />
        </span>
      )}
      <CourseLogo
        logoFileId={row.courseLogoFileId}
        name={row.courseName ?? ''}
        closed={row.courseClosed}
        className="aspect-square w-full rounded-lg"
      />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink dark:text-white">
          {row.courseName}
        </p>
        <p className="text-xs text-ink-muted dark:text-white/50">
          {whereOf(row)}
        </p>
      </div>
    </button>
  );

  // The result: the ranked course with a neighbour either side, as on the list.
  const placed = all.find((row) => keyOf(row) === courseKey);
  const rankedNow = all
    .filter((row) => row.rankOrder != null)
    .sort((a, b) => (a.rankOrder ?? 0) - (b.rankOrder ?? 0));
  const placedIndex = rankedNow.findIndex((row) => keyOf(row) === courseKey);
  const neighbours = rankedNow.slice(
    Math.max(0, placedIndex - 1),
    placedIndex + 2,
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-[60] flex w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-charcoal sm:w-[28rem] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Rank a course"
      >
        {header}

        <div className="flex-1 space-y-4 overflow-y-auto px-5 pb-8 pt-5">
          {course == null ? (
            <p className="text-sm text-ink-muted dark:text-white/60">
              Every course you have played is ranked.
            </p>
          ) : (
            <>
              {courseCard}

              {step !== 'result' && (
                <div className="rounded-xl bg-gray-50 p-4 text-center dark:bg-white/5">
                  <p className="text-base font-medium text-ink dark:text-white">
                    How did you like the course?
                  </p>
                  {step === 'feel' ? (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {SENTIMENTS.map(({ value, label, Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleFeel(value)}
                          disabled={placeCourse.isPending}
                          className="flex flex-col items-center gap-2 rounded-lg bg-white py-3 text-sm text-ink transition hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:bg-white/5 dark:text-white dark:hover:bg-primary/20"
                        >
                          <Icon className="h-6 w-6" />
                          {label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 inline-flex items-center gap-2 text-sm text-ink dark:text-white">
                      {SENTIMENTS.filter((s) => s.value === sentiment).map(
                        ({ value, label, Icon }) => (
                          <React.Fragment key={value}>
                            <Icon className="h-5 w-5" />
                            {label}
                          </React.Fragment>
                        ),
                      )}
                    </p>
                  )}
                </div>
              )}

              {step === 'compare' && opponent != null && (
                <div className="rounded-xl bg-gray-50 p-4 dark:bg-white/5">
                  <p className="text-center text-xs text-ink-muted dark:text-white/50">
                    Rank this course
                  </p>
                  <p className="text-center text-base font-medium text-ink dark:text-white">
                    Which do you like more?
                  </p>
                  <div className="mt-4 flex gap-3">
                    {choiceCard(course, 'course')}
                    {choiceCard(opponent, 'other')}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => startCourse(courseKey)}
                      disabled={placeCourse.isPending}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-deep hover:underline dark:text-primary"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Start over
                    </button>
                    <button
                      type="button"
                      onClick={handlePass}
                      disabled={chosen != null || placeCourse.isPending}
                      title="About the same"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-deep hover:underline dark:text-primary"
                    >
                      Pass
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {step === 'result' && placed != null && (
                <div className="rounded-xl bg-gray-50 p-4 dark:bg-white/5">
                  <p className="flex items-center justify-center gap-2 text-base font-medium text-ink dark:text-white">
                    <CircleCheck className="h-5 w-5 text-primary-deep dark:text-primary" />
                    Ranked!
                  </p>
                  <p className="mt-3 text-3xl font-bold text-ink dark:text-white">
                    #{placed.rankOrder}
                  </p>
                  <p className="text-sm text-ink-muted dark:text-white/60">
                    out of {rankedNow.length}{' '}
                    {rankedNow.length === 1 ? 'course' : 'courses'} ranked by
                    you
                  </p>

                  <ul className="mt-4 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-white dark:divide-white/10 dark:border-white/10 dark:bg-transparent">
                    {neighbours.map((row) => (
                      <li
                        key={keyOf(row)}
                        className={`flex items-center gap-3 px-3 py-2.5 ${
                          keyOf(row) === courseKey ? '' : 'opacity-40'
                        }`}
                      >
                        <span className="w-6 text-sm font-semibold tabular-nums text-ink dark:text-white">
                          {row.rankOrder}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold uppercase text-ink dark:text-white">
                            {row.courseName}
                          </p>
                          <p className="truncate text-xs text-ink-muted dark:text-white/50">
                            {whereOf(row)}
                          </p>
                        </div>
                        <ScoreBadge score={row.score} />
                      </li>
                    ))}
                  </ul>

                  {placed.score == null && (
                    <p className="mt-3 text-center text-xs text-ink-muted dark:text-white/50">
                      Courses get a score once you have ranked 5.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="space-y-3 border-t border-gray-200 px-5 py-4 dark:border-white/10">
          {step === 'result' ? (
            <>
              <p className="text-center text-sm text-ink-muted dark:text-white/60">
                {remaining === 0 ? (
                  'That was your last unranked course.'
                ) : (
                  <>
                    You have{' '}
                    <span className="font-semibold text-ink dark:text-white">
                      {remaining} unranked{' '}
                      {remaining === 1 ? 'course' : 'courses'}
                    </span>
                  </>
                )}
              </p>
              {remaining > 0 && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="h-11 w-full rounded-lg border border-secondary text-sm font-semibold text-ink transition hover:bg-gray-100 dark:border-white/30 dark:text-white dark:hover:bg-white/10"
                >
                  Rank next course
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="h-11 w-full rounded-lg bg-primary text-sm font-semibold text-secondary transition hover:brightness-95"
              >
                Done
              </button>
            </>
          ) : (
            course != null && (
              <button
                type="button"
                onClick={handleSkip}
                disabled={placeCourse.isPending}
                className="inline-flex w-full items-center justify-center gap-1.5 text-sm font-medium text-primary-deep hover:underline dark:text-primary"
              >
                Skip this course
                <ArrowRight className="h-4 w-4" />
              </button>
            )
          )}
        </div>
      </div>
    </>
  );
};

export default RankCourseDrawer;
